import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvoiceSyncState } from "@dallali/shared";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useAuth } from "@/hooks/useAuth";
import { useAssignedPropertyIds } from "@/hooks/useEmployeePropertyAssignments";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  classifyInvoice,
  calculateManagementFees,
  applyVat,
  sumStandaloneFeeGross,
  isOwnerIssuedInvoice,
  type InvoiceKind,
} from "@/lib/financeHelpers";
import { serializeError } from "@/lib/sentry";
import { isValidUUID } from "@/lib/uuid";
import type {
  InvoiceRow,
  ReceiptRow,
  ExpenseRow,
  InvoiceStatus,
  PaymentChannel,
  TransactionType,
  ExpenseType,
  PaymentResponsibility,
  InvoiceWithRelations,
  ReceiptWithRelations,
  ReceiptDetail,
  ExpenseWithRelations,
  ExpenseDetail,
  InvoiceDetail,
  ReceiptFragment,
  TenantFragment,
  OwnerFragment,
  RecipientFragment,
  InvoiceNotificationRow,
} from "@/types/database";
import { sendInvoiceNotification } from "@/services/api";
import { receiptsRepository, type CreateReceiptData } from "@/repositories/receipts";
import { invoicesRepository } from "@/repositories/invoices";
import { expensesRepository } from "@/repositories/expenses";
import { companiesRepository } from "@/repositories/companies";
import { contractsRepository } from "@/repositories/contracts";
import { ownersRepository } from "@/repositories/owners";
import { propertiesRepository } from "@/repositories/properties";
import { tenantsRepository } from "@/repositories/tenants";
import { unitsRepository } from "@/repositories/units";
import type { Database } from "@/integrations/supabase/types";

type InvoiceInsert = Database["public"]["Tables"]["invoices"]["Insert"];
type ReceiptInsert = Database["public"]["Tables"]["receipts"]["Insert"];
type ExpenseInsert = Database["public"]["Tables"]["expenses"]["Insert"];

// Re-export types for consumers
export type Invoice = InvoiceRow;
export type Receipt = ReceiptRow;
export type Expense = ExpenseRow;
export type { InvoiceWithRelations, ReceiptWithRelations, ExpenseWithRelations };

// Invoices
export function useInvoices() {
  const { companyId } = useAuth();
  const { assignedPropertyIds, isLoading: assignmentsLoading } = useAssignedPropertyIds();
  const { hasPermission } = usePermissions();

  // Layer 1 — permission gate. Layer 2 — for employees, always scope by
  // assigned properties even when they hold finance.view.
  const canViewInvoices = hasPermission("finance.view");
  const isEmployee = assignedPropertyIds !== null;

  return useQuery({
    queryKey: ["invoices", companyId, isEmployee ? assignedPropertyIds : "all"],
    queryFn: async (): Promise<InvoiceWithRelations[]> => {
      if (!companyId) return [];
      if (!canViewInvoices) return [];
      if (isEmployee && assignedPropertyIds.length === 0) return [];

      return invoicesRepository.list({
        companyId,
        assignedPropertyIds: isEmployee ? assignedPropertyIds : null,
      });
    },
    enabled: !!companyId && !assignmentsLoading,
  });
}

/**
 * Qoyod sync state per invoice, keyed by invoice id.
 *
 * A SEPARATE query rather than a field on the invoice payload, deliberately:
 * widening `invoiceSchema` risks the invoices list silently falling back to
 * Supabase on a shape mismatch (see repositories/invoices.ts), and employee
 * scoped lists never touch Nest at all. As a side query the worst case is an
 * empty Map — the table renders exactly as before, without badges.
 *
 * Mirrors the pending-verification query already feeding InvoicesTab.
 */
export function useAccountingSyncState() {
  const { companyId } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewInvoices = hasPermission("finance.view");

  return useQuery({
    queryKey: ["accounting-sync-state", companyId],
    queryFn: async (): Promise<Map<string, InvoiceSyncState>> => {
      if (!companyId || !canViewInvoices) return new Map();
      const rows = await invoicesRepository.listAccountingSyncState();
      return new Map(rows.map((r) => [r.invoiceId, r]));
    },
    enabled: !!companyId && canViewInvoices,
    // The worker drains every minute and the accountant approves weekly — a
    // slightly stale badge is fine, refetching on every focus is not.
    staleTime: 60_000,
  });
}

export function useInvoice(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["invoice", id],
    queryFn: async (): Promise<InvoiceDetail | null> => {
      if (!id || !companyId) return null;

      return invoicesRepository.getById(id, companyId);
    },
    // isValidUUID: URL-driven id — a malformed value otherwise reaches
    // Postgres as a 22P02 error (the #3297 class). See src/lib/uuid.ts.
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

// Receipts
export function useReceipts() {
  const { companyId } = useAuth();
  const { assignedPropertyIds, isLoading: assignmentsLoading } = useAssignedPropertyIds();
  const { hasPermission } = usePermissions();

  // Layer 1 — permission gate. Layer 2 — for employees, always scope by
  // assigned properties even when they hold finance.view.
  const canViewReceipts = hasPermission("finance.view");
  const isEmployee = assignedPropertyIds !== null;

  return useQuery({
    queryKey: ["receipts", companyId, isEmployee ? assignedPropertyIds : "all"],
    queryFn: async (): Promise<ReceiptWithRelations[]> => {
      if (!companyId) return [];
      if (!canViewReceipts) return [];
      if (isEmployee && assignedPropertyIds.length === 0) return [];

      return receiptsRepository.list({
        companyId,
        assignedPropertyIds: isEmployee ? assignedPropertyIds : null,
      });
    },
    enabled: !!companyId && !assignmentsLoading,
  });
}

export function useReceipt(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["receipt", id],
    queryFn: async (): Promise<ReceiptDetail | null> => {
      if (!id || !companyId) return null;
      
      // First fetch receipt with basic relations
      const receipt = await receiptsRepository.getById(id, companyId);

      if (!receipt) return null;

      // Fetch tenant data from tenants table via contract
      let tenantFull: TenantFragment | null = null;
      const contractData = receipt.contract as unknown as { tenant_id?: string | null; owner_id?: string | null } | null;
      
      if (contractData?.tenant_id) {
        tenantFull = await tenantsRepository.getReceiptContact(
          contractData.tenant_id,
          companyId
        );
      }

      // Fetch owner data if contract has owner_id
      let owner: OwnerFragment | null = null;
      if (contractData?.owner_id) {
        const ownerData = await ownersRepository.getReceiptContact(
          contractData.owner_id,
          companyId
        );
        owner = ownerData as OwnerFragment | null;
      }

      // Build full_name from tenant data for backward compat
      const tenantFullName = tenantFull 
        ? (tenantFull.tenant_type === 'commercial' 
            ? (tenantFull.commercial_name || tenantFull.commercial_name_en || '---')
            : `${tenantFull.first_name || ''} ${tenantFull.last_name || ''}`.trim() || '---')
        : null;

      return {
        ...receipt,
        tenant: tenantFull ? {
          full_name: tenantFullName,
          phone: tenantFull.phone,
          email: tenantFull.email,
          address: tenantFull.address,
        } : null,
        tenantFull,
        owner,
      } as unknown as ReceiptDetail;
    },
    // isValidUUID: URL-driven id — see src/lib/uuid.ts (the #3297 class).
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

export type { CreateReceiptData } from "@/repositories/receipts";

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (receiptData: CreateReceiptData) => {
      if (!companyId) throw new Error("No company ID");
      return receiptsRepository.create(companyId, receiptData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-financial-data"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-revenue-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["finance-summary-stats"] });
      queryClient.invalidateQueries({ queryKey: ["finance-totals-summary"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-vs-expenses-chart"] });
      toast.success(t("تم إضافة الدفعة بنجاح", "Payment added successfully"));
    },
    onError: (error: unknown) => {
      console.error("Create receipt error:", serializeError(error));
      toast.error(t("حدث خطأ أثناء إضافة الدفعة", "Error adding payment"));
    },
  });
}

// Expenses
export function useExpenses() {
  const { companyId } = useAuth();
  const { assignedPropertyIds, isLoading: assignmentsLoading } = useAssignedPropertyIds();
  const { hasPermission } = usePermissions();

  // Layer 1 — permission gate. Layer 2 — for employees, always scope by
  // assigned properties even when they hold finance.view.
  const canViewExpenses = hasPermission("finance.view");
  const isEmployee = assignedPropertyIds !== null;

  return useQuery({
    queryKey: ["expenses", companyId, isEmployee ? assignedPropertyIds : "all"],
    queryFn: async (): Promise<ExpenseWithRelations[]> => {
      if (!companyId) return [];
      if (!canViewExpenses) return [];
      if (isEmployee && assignedPropertyIds.length === 0) return [];

      return expensesRepository.list({
        companyId,
        assignedPropertyIds: isEmployee ? assignedPropertyIds : null,
      });
    },
    enabled: !!companyId && !assignmentsLoading,
  });
}

export function useExpense(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["expense", id],
    queryFn: async (): Promise<ExpenseDetail | null> => {
      if (!id || !companyId) return null;
      return expensesRepository.getById(id, companyId);
    },
    // isValidUUID: URL-driven id — see src/lib/uuid.ts (the #3297 class).
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

export interface CreateExpenseData {
  property_id?: string;
  unit_id?: string;
  contract_id?: string;
  expense_date: string;
  expense_type: ExpenseType;
  payment_responsibility: PaymentResponsibility;
  amount: number;
  vat_rate?: number;
  vendor_name?: string;
  description: string;
  invoice_url?: string;
  images?: string[];
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (expenseData: CreateExpenseData) => {
      if (!companyId) throw new Error("No company ID");

      // Use ?? not || so an explicit 0% rate is honoured. With `|| 15`, a saved
      // vat_rate of 0 is falsy → defaulted to 15, charging phantom VAT (the
      // expense stored 0% yet the total added 15% — ticket 86exwj1jg).
      const vatAmount = (expenseData.amount * (expenseData.vat_rate ?? 15)) / 100;
      const totalAmount = expenseData.amount + vatAmount;

      const insertData: ExpenseInsert = {
        ...expenseData,
        company_id: companyId,
        vat_amount: vatAmount,
        total_amount: totalAmount,
      };

      return expensesRepository.create(insertData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-revenue-expenses"] });
      toast.success(t("تم إضافة المصروف بنجاح", "Expense added successfully"));
    },
    onError: (error: unknown) => {
      console.error("Create expense error:", serializeError(error));
      toast.error(t("حدث خطأ أثناء إضافة المصروف", "Error adding expense"));
    },
  });
}

export interface UpdateExpenseData {
  id: string;
  expense_date?: string;
  expense_type?: ExpenseType;
  payment_responsibility?: PaymentResponsibility;
  amount?: number;
  vat_rate?: number;
  vendor_name?: string;
  description?: string;
  invoice_url?: string;
  images?: string[];
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, amount, vat_rate, ...rest }: UpdateExpenseData) => {
      if (!companyId) throw new Error("No company ID");

      const baseAmount = amount ?? 0;
      const vatRate = vat_rate ?? 0;
      const vatAmount = (baseAmount * vatRate) / 100;
      const totalAmount = baseAmount + vatAmount;

      await expensesRepository.update(id, companyId, {
        ...rest,
        amount: baseAmount,
        vat_rate: vatRate,
        vat_amount: vatAmount,
        total_amount: totalAmount,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      // The detail view's query key is ["expense", id] (singular) — NOT a prefix
      // of ["expenses"], so without this the detail screen keeps the stale row.
      queryClient.invalidateQueries({ queryKey: ["expense"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-revenue-expenses"] });
      toast.success(t("تم تحديث المصروف بنجاح", "Expense updated successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء تحديث المصروف", "Error updating expense"));
    },
  });
}

/**
 * Soft-delete a receipt AND reverse its effect on the parent invoice — all
 * inside a single Postgres function (delete_receipt_and_reconcile) so the
 * receipt soft-delete + invoice paid_amount/balance/status + scheduled_payment
 * sync can never be observed mid-reconciliation.
 *
 * The RPC mirrors the same status rules pinned in
 * src/lib/__tests__/financeHelpers.test.ts (see
 * computeInvoiceStatusAfterPaidAmountChange) so the SQL and the TS helper
 * agree on what an invoice should become after a payment is removed.
 *
 * Routed through `receiptsRepository.deleteAndReconcile` — Nest-first
 * (`DELETE /receipts/:id`, which wraps the atomic RPC) with a Supabase-RPC
 * fallback. company_id is derived server-side by the endpoint.
 *
 * RPC: public.delete_receipt_and_reconcile (supabase/baseline/public_schema.sql).
 */
export function useDeleteReceipt() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID");
      await receiptsRepository.deleteAndReconcile(companyId, id);
    },
    onSuccess: () => {
      // Match AddPaymentDialog's invalidation set — every view that surfaces
      // receipts, invoices, or summary totals needs to refetch.
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      queryClient.invalidateQueries({ queryKey: ["finance-summary-stats"] });
      queryClient.invalidateQueries({ queryKey: ["finance-totals-summary"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled_payments"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-payments"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-receipts"] });
      queryClient.invalidateQueries({ queryKey: ["owner-financials"] });
      toast.success(t("تم حذف الإيصال بنجاح", "Receipt deleted successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء حذف الإيصال", "Error deleting receipt"));
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID");
      await expensesRepository.softDelete(id, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      // Also bust the singular ["expense", id] detail cache (a detail page may be
      // mounted when a delete fires from a list view).
      queryClient.invalidateQueries({ queryKey: ["expense"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-revenue-expenses"] });
      toast.success(t("تم حذف المصروف بنجاح", "Expense deleted successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء حذف المصروف", "Error deleting expense"));
    },
  });
}

// Update expense images
export interface UpdateExpenseImagesData {
  expenseId: string;
  images: string[];
}

export function useUpdateExpenseImages() {
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ expenseId, images }: UpdateExpenseImagesData) => {
      return expensesRepository.updateImages(expenseId, images);
    },
    onSuccess: () => {
      // Prefix invalidation — consistent with useUpdateExpense/useDeleteExpense.
      queryClient.invalidateQueries({ queryKey: ["expense"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (error: unknown) => {
      console.error("Update expense images error:", serializeError(error));
    },
  });
}

// ─── Generate Invoice from Expense ───────────────────────────────

export interface GenerateInvoiceFromExpenseData {
  expenseId: string;
  expense: ExpenseWithRelations & {
    contract_id?: string | null;
    property_id?: string | null;
    total_amount: number;
    description: string;
    expense_date: string;
    payment_responsibility: PaymentResponsibility;
  };
}

export function useGenerateInvoiceFromExpense() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ expenseId, expense }: GenerateInvoiceFromExpenseData) => {
      if (!companyId) throw new Error("No company ID");

      return expensesRepository.generateInvoiceFromExpense({
        expenseId,
        companyId,
        payment_responsibility: expense.payment_responsibility,
        contract_id: expense.contract_id,
        property_id: expense.property_id,
        unit_id: expense.unit_id,
        total_amount: expense.total_amount,
        description: expense.description,
        expense_date: expense.expense_date,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      toast.success(t("تم إصدار الفاتورة بنجاح", "Invoice generated successfully"));
    },
    onError: (error: unknown) => {
      console.error("Generate invoice from expense error:", serializeError(error));
      toast.error(t("حدث خطأ أثناء إصدار الفاتورة", "Error generating invoice"));
    },
  });
}

// ─── Financial Stats ──────────────────────────────────────────────

export function useFinancialStats() {
  const { companyId } = useAuth();
  const { hasPermission } = usePermissions();

  return useQuery({
    queryKey: ["financial-stats", companyId],
    queryFn: async () => {
      if (!companyId) {
        return {
          totalInvoices: 0,
          totalReceipts: 0,
          totalExpenses: 0,
          pendingAmount: 0,
          invoicesCount: 0,
          receiptsCount: 0,
          expensesCount: 0,
          expenseReceiptsCount: 0,
          managementFees: 0,
          totalSales: 0,
        };
      }
      if (!hasPermission("finance.view")) {
        return {
          totalInvoices: 0,
          totalReceipts: 0,
          totalExpenses: 0,
          pendingAmount: 0,
          invoicesCount: 0,
          receiptsCount: 0,
          expensesCount: 0,
          expenseReceiptsCount: 0,
          managementFees: 0,
          totalSales: 0,
        };
      }
      
      // Get company settings for management fee
      const company =
        await companiesRepository.getManagementFeeSettings(companyId);

      const feeType = company?.management_fee_type || "percentage";
      const feeValue = company?.management_fee_value || 10;
      
      const invoices = await invoicesRepository.listStats(companyId);

      const receipts = await receiptsRepository.listStats(companyId);

      const expenses = await expensesRepository.listStats(companyId);

      const saleContracts = await contractsRepository.listSaleTotals(companyId);

      // Count expense receipts (expenses that have been invoiced AND the invoice has been paid)
      const invoicedExpenseIds = (expenses
        ?.filter(e => e.is_invoiced && e.generated_invoice_id)
        .map(e => e.generated_invoice_id) ?? []) as string[];

      let expenseReceiptsCount = 0;
      if (invoicedExpenseIds.length > 0) {
        const paidExpenseInvoices = await invoicesRepository.listPaidByIds(invoicedExpenseIds, companyId);
        expenseReceiptsCount = paidExpenseInvoices.length;
      }

      const totalInvoices = invoices?.reduce((sum, i) => sum + (i.total_amount || 0), 0) || 0;
      const totalReceipts = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
      const totalExpenses = expenses?.reduce((sum, e) => sum + (e.total_amount || 0), 0) || 0;
      const pendingAmount = invoices?.reduce((sum, i) => sum + (i.balance || 0), 0) || 0;
      const totalSales = saleContracts?.reduce((sum, c) => sum + (c.total_amount || 0), 0) || 0;

      const managementFees = calculateManagementFees(feeType, feeValue, totalReceipts, receipts.length);

      return {
        totalInvoices,
        totalReceipts,
        totalExpenses,
        pendingAmount,
        invoicesCount: invoices?.length || 0,
        receiptsCount: receipts?.length || 0,
        expensesCount: expenses?.length || 0,
        expenseReceiptsCount,
        managementFees,
        totalSales,
      };
    },
    enabled: !!companyId,
  });
}

// ─── Finance Summary Stats ────────────────────────────────────────

export interface FinanceSummaryStats {
  rent: { collected: number; total: number; count?: number };
  sale: { collected: number; total: number; count?: number };
  expenses: { paid: number; total: number };
  marketingFee: { collected: number; total: number; count?: number };
  contractManagementFee: { collected: number; total: number; count?: number };
  brokerageFee: { collected: number; total: number; count?: number };
  managementFee: { collected: number; total: number; count?: number };
  // Retained for back-compat with non-UI consumers; not rendered in FinanceSummaryTab.
  contractFee: { collected: number; total: number };
  ejarFee: { paid: number; total: number };
  subscriptionFee: { collected: number; total: number };
}

export function useFinanceSummaryStats(startDate?: Date, endDate?: Date) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["finance-summary-stats", companyId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async (): Promise<FinanceSummaryStats> => {
      if (!companyId) {
        return {
          rent: { collected: 0, total: 0 },
          sale: { collected: 0, total: 0 },
          expenses: { paid: 0, total: 0 },
          marketingFee: { collected: 0, total: 0 },
          contractManagementFee: { collected: 0, total: 0 },
          brokerageFee: { collected: 0, total: 0 },
          managementFee: { collected: 0, total: 0 },
          contractFee: { collected: 0, total: 0 },
          ejarFee: { paid: 0, total: 0 },
          subscriptionFee: { collected: 0, total: 0 },
        };
      }

      const fromIso = startDate ? startDate.toISOString() : undefined;
      const toIso = endDate ? endDate.toISOString() : undefined;

      // VAT lives on companies.vat_rate (percentage, e.g. 15). Contract fee
      // columns are stored NET of VAT, but receipts pay invoices at GROSS
      // (subtotal + vat_amount). Without scaling Total by (1 + vat/100), the
      // Total < Collected for any paid fee — the card renders as 100%+ even
      // when only a fraction has been collected.
      const [invoices, receipts, expenses, contracts, company] =
        await Promise.all([
          invoicesRepository.listSummary(companyId, { startDate: fromIso, endDate: toIso }),
          receiptsRepository.listSummary(companyId, { startDate: fromIso, endDate: toIso }),
          expensesRepository.listSummary(companyId, { startDate: fromIso, endDate: toIso }),
          contractsRepository.listFeeTotals(companyId, { startDate: fromIso, endDate: toIso }),
          companiesRepository.getVatRate(companyId),
        ]);

      const contractsById = new Map((contracts ?? []).map((c) => [c.id, c.contract_type] as const));

      type Kind = InvoiceKind;

      const classify = (inv: { contract_id: string | null; description: string | null }): Kind => {
        const ct =
          (inv.contract_id ? contractsById.get(inv.contract_id) : undefined) ??
          undefined;
        return classifyInvoice(ct, inv.description);
      };

      const invoicesList = invoices ?? [];
      const receiptsList = receipts ?? [];
      const expensesList = expenses ?? [];

      const invoiceKindById = new Map<string, Kind>();
      for (const inv of invoicesList) {
        invoiceKindById.set(inv.id, classify({ contract_id: inv.contract_id, description: inv.description }));
      }

      const sum = (nums: number[]) => nums.reduce((acc, n) => acc + (n || 0), 0);

      const contractsList = contracts ?? [];
      type ContractFeeCol = "marketing_fee" | "contract_management_fee" | "brokerage_fee" | "management_fee";
      const sumContractColumn = (col: ContractFeeCol) =>
        applyVat(sum(contractsList.map((c) => c[col] ?? 0)), company?.vat_rate);

      // Fees billed outside the per-contract model (e.g. monthly aggregate
      // management bills with contract_id = NULL). Their gross value already
      // sits on invoice.total_amount — no need to re-apply VAT. Without this
      // sum, the cards would show Collected > Total for any company that
      // generates such invoices: receipts on those invoices still count via
      // sumReceiptAmount (description-classified), but the per-contract Total
      // can't see them. Folding them into Total closes that gap.
      // Pure logic extracted to financeHelpers so it's unit-testable.
      const standaloneFor = (kind: Kind) =>
        sumStandaloneFeeGross(invoicesList, invoiceKindById, kind);

      const sumInvoiceTotal = (kind: Kind) =>
        sum(
          invoicesList
            .filter((i) => invoiceKindById.get(i.id) === kind)
            .map((i) => i.total_amount || 0)
        );

      const sumReceiptAmount = (kind: Kind) =>
        sum(
          receiptsList
            .filter((r) => {
              const fromInvoice = r.invoice_id ? invoiceKindById.get(r.invoice_id) : undefined;
              if (fromInvoice) return fromInvoice === kind;
              if (kind === "rent") return r.transaction_type === "rent";
              if (kind === "sale") return r.transaction_type === "other";
              if (kind === "subscription_fee") return r.transaction_type === "service";
              return false;
            })
            .map((r) => r.amount || 0)
        );

      // Calculate expense-generated invoice stats
      // Get IDs of invoices that were generated from expenses
      const expenseGeneratedInvoiceIds = new Set(
        expensesList
          .filter((e) => e.is_invoiced && e.generated_invoice_id)
          .map((e) => e.generated_invoice_id as string)
      );

      // Total: Sum of invoice amounts for invoices generated from expenses
      const expenseInvoicesTotal = sum(
        invoicesList
          .filter((inv) => expenseGeneratedInvoiceIds.has(inv.id))
          .map((inv) => inv.total_amount || 0)
      );

      // Collected: Sum of receipts for invoices generated from expenses
      const expenseInvoicesCollected = sum(
        receiptsList
          .filter((r) => r.invoice_id && expenseGeneratedInvoiceIds.has(r.invoice_id))
          .map((r) => r.amount || 0)
      );

      const countInvoices = (kind: Kind) =>
        invoicesList.filter((i) => invoiceKindById.get(i.id) === kind).length;

      return {
        rent: { collected: sumReceiptAmount("rent"), total: sumInvoiceTotal("rent"), count: countInvoices("rent") },
        sale: { collected: sumReceiptAmount("sale"), total: sumInvoiceTotal("sale"), count: countInvoices("sale") },
        expenses: { paid: expenseInvoicesCollected, total: expenseInvoicesTotal },
        // Fee card Total = per-contract column (gross, via applyVat) + standalone
        // fee invoices billed without a contract_id (already gross on the row).
        // NOTE on contractManagementFee: the contract column is named
        // `contract_management_fee` but classifyInvoice returns the InvoiceKind
        // "contract_fee" for descriptions like "رسوم إدارة العقود" — historical
        // naming inconsistency between the schema and the classifier enum.
        // The two refer to the same business concept.
        marketingFee: {
          collected: sumReceiptAmount("marketing_fee"),
          total: sumContractColumn("marketing_fee") + standaloneFor("marketing_fee"),
          count: countInvoices("marketing_fee"),
        },
        // contractManagementFee.total: invoice-based (like rent/sale), NOT the
        // contract-column path the other 3 cards use. Since the 2026-07-06
        // fee swap, contract_management_fee is back to ONE invoice per
        // contract — but the card stays invoice-based: it keeps the total
        // consistent with the (now per-year) managementFee card, and
        // sumInvoiceTotal covers standalone invoices without double-counting
        // (see below). It aggregates however many live management-fee
        // invoices actually exist — INCLUDING
        // standalone ones (contract_id IS NULL), since it filters by
        // InvoiceKind only, not contract_id. Do NOT also add standaloneFor()
        // here (unlike the 3 column-based cards below) — sumInvoiceTotal
        // already covers those rows; adding standaloneFor on top would
        // double-count every standalone contract_fee invoice.
        contractManagementFee: {
          collected: sumReceiptAmount("contract_fee"),
          total: sumInvoiceTotal("contract_fee"),
          count: countInvoices("contract_fee"),
        },
        brokerageFee: {
          collected: sumReceiptAmount("brokerage_fee"),
          total: sumContractColumn("brokerage_fee") + standaloneFor("brokerage_fee"),
          count: countInvoices("brokerage_fee"),
        },
        // "Management fees" = the CEO's view of ongoing portfolio/property
        // management revenue. Aggregates BOTH InvoiceKinds:
        //   - property_management_fee: per-contract ongoing fee (modern)
        //   - management_fee: generic management billing (legacy +
        //     standalone monthly aggregate bills with contract_id IS NULL)
        // Total is invoice-based (like contractManagementFee above) since the
        // 2026-07-06 fee swap: contracts.property_management_fee now stores a
        // PER-YEAR amount and a contract carries MULTIPLE live property-mgmt
        // invoices (one per contract year) — summing the raw column once per
        // contract would under-report a multi-year contract's total by a
        // factor of N years. sumInvoiceTotal aggregates however many live
        // invoices actually exist, INCLUDING standalone (contract_id IS NULL)
        // rows — do NOT also add standaloneFor() here, that would
        // double-count them. Legacy contracts whose management_fee column has
        // no matching invoice (the historical duplicate-write phantom) no
        // longer inflate the Total.
        managementFee: {
          collected: sumReceiptAmount("management_fee") + sumReceiptAmount("property_management_fee"),
          total: sumInvoiceTotal("management_fee") + sumInvoiceTotal("property_management_fee"),
          count: countInvoices("management_fee") + countInvoices("property_management_fee"),
        },
        contractFee: { collected: sumReceiptAmount("contract_fee"), total: sumInvoiceTotal("contract_fee") },
        ejarFee: { paid: sumReceiptAmount("ejar_fee"), total: sumInvoiceTotal("ejar_fee") },
        subscriptionFee: { collected: sumReceiptAmount("subscription_fee"), total: sumInvoiceTotal("subscription_fee") },
      };
    },
    enabled: !!companyId,
  });
}

// ─── Chart Data Hooks ─────────────────────────────────────────────

interface MonthName {
  ar: string;
  en: string;
}

const MONTH_NAMES: MonthName[] = [
  { ar: "يناير", en: "Jan" },
  { ar: "فبراير", en: "Feb" },
  { ar: "مارس", en: "Mar" },
  { ar: "أبريل", en: "Apr" },
  { ar: "مايو", en: "May" },
  { ar: "يونيو", en: "Jun" },
  { ar: "يوليو", en: "Jul" },
  { ar: "أغسطس", en: "Aug" },
  { ar: "سبتمبر", en: "Sep" },
  { ar: "أكتوبر", en: "Oct" },
  { ar: "نوفمبر", en: "Nov" },
  { ar: "ديسمبر", en: "Dec" },
];

export interface MonthlyFinancialDataPoint {
  month: string;
  monthEn: string;
  invoices: number;
  payments: number;
}

export function useMonthlyFinancialData(months: number = 6) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["monthly-financial-data", companyId, months],
    queryFn: async (): Promise<MonthlyFinancialDataPoint[]> => {
      if (!companyId) return [];

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months + 1);
      startDate.setDate(1);

      const [invoices, receipts] = await Promise.all([
        invoicesRepository.listMonthly(companyId, startDate.toISOString().split("T")[0]),
        receiptsRepository.listMonthly(companyId, startDate.toISOString().split("T")[0]),
      ]);

      const monthlyData: MonthlyFinancialDataPoint[] = [];
      
      for (let i = 0; i < months; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - months + 1 + i);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0);

        const monthInvoices = invoices
          .filter((inv) => {
            const d = new Date(inv.invoice_date ?? "");
            return d >= monthStart && d <= monthEnd;
          })
          .reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;

        const monthPayments = receipts
          ?.filter((r) => {
            const d = new Date(r.payment_date);
            return d >= monthStart && d <= monthEnd;
          })
          .reduce((sum, r) => sum + (r.amount || 0), 0) || 0;

        monthlyData.push({
          month: MONTH_NAMES[monthIndex].ar,
          monthEn: MONTH_NAMES[monthIndex].en,
          invoices: monthInvoices,
          payments: monthPayments,
        });
      }

      return monthlyData;
    },
    enabled: !!companyId,
  });
}

export interface MonthlyRevenueExpensePoint {
  month: string;
  monthEn: string;
  revenue: number;
  expenses: number;
}

export function useMonthlyRevenueExpenses(months: number = 6) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["monthly-revenue-expenses", companyId, months],
    queryFn: async (): Promise<MonthlyRevenueExpensePoint[]> => {
      if (!companyId) return [];

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months + 1);
      startDate.setDate(1);

      const [receipts, expenses] = await Promise.all([
        receiptsRepository.listMonthly(companyId, startDate.toISOString().split("T")[0]),
        expensesRepository.listMonthly(companyId, startDate.toISOString().split("T")[0]),
      ]);

      const monthlyData: MonthlyRevenueExpensePoint[] = [];
      
      for (let i = 0; i < months; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - months + 1 + i);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0);

        const monthRevenue = receipts
          ?.filter((r) => {
            const d = new Date(r.payment_date);
            return d >= monthStart && d <= monthEnd;
          })
          .reduce((sum, r) => sum + (r.amount || 0), 0) || 0;

        const monthExpenses = expenses
          ?.filter((e) => {
            // Null expense_date never falls in a month window (new Date(null)
            // is the 1970 epoch) — make the exclusion explicit.
            if (!e.expense_date) return false;
            const d = new Date(e.expense_date);
            return d >= monthStart && d <= monthEnd;
          })
          .reduce((sum, e) => sum + (e.total_amount || 0), 0) || 0;

        monthlyData.push({
          month: MONTH_NAMES[monthIndex].ar,
          monthEn: MONTH_NAMES[monthIndex].en,
          revenue: monthRevenue,
          expenses: monthExpenses,
        });
      }

      return monthlyData;
    },
    enabled: !!companyId,
  });
}

// ─── Occupancy Hooks ──────────────────────────────────────────────

export interface OccupancyDataPoint {
  month: string;
  monthEn: string;
  rate: number;
}

export function useOccupancyData(months: number = 12) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["occupancy-data", companyId, months],
    queryFn: async (): Promise<OccupancyDataPoint[]> => {
      if (!companyId) return [];

      const totalUnits = await unitsRepository.count(companyId);

      const contracts = await contractsRepository.listOccupancyWindows(companyId);

      const monthlyData: OccupancyDataPoint[] = [];
      
      for (let i = 0; i < months; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - months + 1 + i);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        
        const monthMiddle = new Date(year, monthIndex, 15);

        const occupiedUnits = contracts?.filter((c) => {
          const startDate = new Date(c.start_date);
          const endDate = new Date(c.end_date);
          return startDate <= monthMiddle && endDate >= monthMiddle;
        }).length || 0;

        const rate = totalUnits && totalUnits > 0 
          ? Math.round((occupiedUnits / totalUnits) * 100)
          : 0;

        monthlyData.push({
          month: MONTH_NAMES[monthIndex].ar,
          monthEn: MONTH_NAMES[monthIndex].en,
          rate,
        });
      }

      return monthlyData;
    },
    enabled: !!companyId,
  });
}

export interface CurrentOccupancy {
  total: number;
  occupied: number;
  vacant: number;
  rate: number;
}

export function useCurrentOccupancy() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["current-occupancy", companyId],
    queryFn: async (): Promise<CurrentOccupancy> => {
      if (!companyId) return { total: 0, occupied: 0, vacant: 0, rate: 0 };

      const totalUnits = await unitsRepository.count(companyId);

      const occupiedUnits = await unitsRepository.countRented(companyId);

      const total = totalUnits || 0;
      const occupied = occupiedUnits || 0;
      const vacant = total - occupied;
      const rate = total > 0 ? Math.round((occupied / total) * 100) : 0;

      return { total, occupied, vacant, rate };
    },
    enabled: !!companyId,
  });
}

// ─── Owner Payouts ────────────────────────────────────────────────

export interface OwnerPayoutData {
  ownerId: string;
  ownerName: string;
  properties: { id: string; name: string }[];
  /** Rent/sale receipts only — the owner's own money. Never includes a
   *  Dallali fee receipt billed against the same property. */
  totalRevenue: number;
  /** Real fee invoices (management/brokerage/marketing/etc.) billed against
   *  the owner's properties — recorded on the payout for audit purposes
   *  only. NOT deducted from netAmount: fees are invoiced and collected
   *  from the owner separately, not netted out of the rent/sale payout. */
  managementFees: number;
  netAmount: number;
}

export function useOwnerPayouts() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["owner-payouts", companyId],
    queryFn: async (): Promise<OwnerPayoutData[]> => {
      if (!companyId) return [];

      const owners = await ownersRepository.listActiveIdName(companyId);

      if (owners.length === 0) return [];

      const properties = await propertiesRepository.listWithOwner(companyId);

      if (properties.length === 0) {
        return owners.map((o) => ({
          ownerId: o.id,
          ownerName: o.full_name,
          properties: [],
          totalRevenue: 0,
          managementFees: 0,
          netAmount: 0,
        }));
      }

      const propertyIds = properties.map((p) => p.id);

      const [receipts, invoices, payouts] = await Promise.all([
        receiptsRepository.listForOwnerPayouts(companyId),
        invoicesRepository.listForOwnerPayouts(companyId, propertyIds),
        expensesRepository.listOwnerPayouts(companyId),
      ]);

      const invoiceById = new Map(invoices.map((inv) => [inv.id, inv]));
      const classifyRow = (inv: { contract: { contract_type: string | null } | null; description: string | null }) =>
        classifyInvoice(inv.contract?.contract_type ?? undefined, inv.description);

      const ownerMap = new Map<string, OwnerPayoutData>();

      owners.forEach((owner) => {
        ownerMap.set(owner.id, {
          ownerId: owner.id,
          ownerName: owner.full_name,
          properties: [],
          totalRevenue: 0,
          managementFees: 0,
          netAmount: 0,
        });
      });

      properties.forEach((property) => {
        const ownerData = ownerMap.get(property.owner_id!);
        if (!ownerData) return;
        ownerData.properties.push({ id: property.id, name: property.name });
      });

      // Owner revenue = rent/sale receipts only. A receipt with no linked
      // invoice (or one classified as anything other than rent/sale — e.g.
      // a brokerage-fee invoice on the same property) never counts here.
      receipts.forEach((receipt) => {
        if (!receipt.property_id || !receipt.amount) return;
        const property = properties.find((p) => p.id === receipt.property_id);
        if (!property || !property.owner_id) return;
        const ownerData = ownerMap.get(property.owner_id);
        if (!ownerData) return;

        const invoice = receipt.invoice_id ? invoiceById.get(receipt.invoice_id) : undefined;
        const kind = invoice ? classifyRow(invoice) : undefined;
        if (kind === "rent" || kind === "sale") {
          ownerData.totalRevenue += Number(receipt.amount) || 0;
        }
      });

      // Management fee = the ACTUAL fee invoices billed against the
      // owner's properties (management/brokerage/marketing/contract/ejar/
      // subscription fees), never a percentage-of-revenue guess. Rent,
      // sale, and deposit invoices are the owner's own money, not a fee.
      invoices.forEach((invoice) => {
        if (!invoice.property_id || !invoice.total_amount) return;
        const property = properties.find((p) => p.id === invoice.property_id);
        if (!property || !property.owner_id) return;
        const ownerData = ownerMap.get(property.owner_id);
        if (!ownerData) return;

        const kind = classifyRow(invoice);
        if (kind !== "unknown" && !isOwnerIssuedInvoice(kind)) {
          ownerData.managementFees += Number(invoice.total_amount) || 0;
        }
      });

      const paidByOwner = new Map<string, number>();
      payouts.forEach((payout) => {
        const current = paidByOwner.get(payout.owner_id) || 0;
        paidByOwner.set(payout.owner_id, current + Number(payout.amount || 0));
      });

      const result: OwnerPayoutData[] = [];
      ownerMap.forEach((owner) => {
        const alreadyPaid = paidByOwner.get(owner.ownerId) || 0;
        owner.netAmount = owner.totalRevenue - alreadyPaid;
        result.push(owner);
      });

      return result.sort((a, b) => b.netAmount - a.netAmount);
    },
    enabled: !!companyId,
  });
}

export function useRecordOwnerPayout() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (data: {
      ownerId: string;
      amount: number;
      revenueAmount: number;
      expensesAmount: number;
      managementFee: number;
      notes?: string;
      paymentMethod?: string;
      referenceNumber?: string;
    }) => {
      if (!companyId) throw new Error("Company ID is required");
      await expensesRepository.createOwnerPayout(companyId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-payouts"] });
    },
  });
}

// ─── Filtered Financial Stats ─────────────────────────────────────

export interface FilteredFinancialStats {
  totalInvoices: number;
  totalRevenue: number;
  totalExpenses: number;
  collected: number;
  pending: number;
  revenueChange: number;
  expenseChange: number;
}

export function useFilteredFinancialStats(startDate?: Date, endDate?: Date) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["filtered-financial-stats", companyId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async (): Promise<FilteredFinancialStats> => {
      if (!companyId) {
        return {
          totalInvoices: 0,
          totalRevenue: 0,
          totalExpenses: 0,
          collected: 0,
          pending: 0,
          revenueChange: 0,
          expenseChange: 0,
        };
      }

      const startDateStr = startDate ? startDate.toISOString().split("T")[0] : undefined;
      const endDateStr = endDate ? endDate.toISOString().split("T")[0] : undefined;

      const [invoices, receipts, expenses] = await Promise.all([
        invoicesRepository.listFinancial(companyId, { startDate: startDateStr, endDate: endDateStr }),
        receiptsRepository.listFinancial(companyId, { startDate: startDateStr, endDate: endDateStr }),
        expensesRepository.listFinancial(companyId, { startDate: startDateStr, endDate: endDateStr }),
      ]);

      const totalInvoices = invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0);
      const totalRevenue = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
      const totalExpenses = expenses.reduce((sum, e) => sum + (e.total_amount || 0), 0);
      const collected = invoices.reduce((sum, i) => sum + (i.paid_amount || 0), 0);
      const pending = invoices.reduce((sum, i) => sum + (i.balance || 0), 0);

      let revenueChange = 0;
      let expenseChange = 0;

      if (startDate && endDate) {
        const periodLength = endDate.getTime() - startDate.getTime();
        const prevStart = new Date(startDate.getTime() - periodLength);
        const prevEnd = new Date(startDate.getTime() - 1);

        const [prevReceipts, prevExpenses] = await Promise.all([
          receiptsRepository.listFinancial(companyId, {
            startDate: prevStart.toISOString().split("T")[0],
            endDate: prevEnd.toISOString().split("T")[0],
          }),
          expensesRepository.listFinancial(companyId, {
            startDate: prevStart.toISOString().split("T")[0],
            endDate: prevEnd.toISOString().split("T")[0],
          }),
        ]);

        const prevRevenue = prevReceipts.reduce((sum, r) => sum + (r.amount || 0), 0);
        const prevExpense = prevExpenses.reduce((sum, e) => sum + (e.total_amount || 0), 0);

        if (prevRevenue > 0) {
          revenueChange = Math.round(((totalRevenue - prevRevenue) / prevRevenue) * 100);
        }
        if (prevExpense > 0) {
          expenseChange = Math.round(((totalExpenses - prevExpense) / prevExpense) * 100);
        }
      }

      return {
        totalInvoices,
        totalRevenue,
        totalExpenses,
        collected,
        pending,
        revenueChange,
        expenseChange,
      };
    },
    enabled: !!companyId,
  });
}

// ─── Invoice Notification Hooks ──────────────────────────────────────────────

export function useInvoiceNotificationHistory(invoiceId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["invoice-notifications", invoiceId, companyId],
    queryFn: async (): Promise<InvoiceNotificationRow[]> => {
      if (!companyId || !invoiceId) return [];
      return invoicesRepository.listNotifications(invoiceId, companyId);
    },
    enabled: !!companyId && !!invoiceId,
  });
}

export function useSendInvoiceNotification() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  return useDemoSafeMutation({
    mutationFn: async (params: Parameters<typeof sendInvoiceNotification>[0]) => {
      return sendInvoiceNotification(params);
    },
    onSuccess: (_data, variables) => {
      toast.success(t("تم إرسال الفاتورة بنجاح", "Invoice sent successfully"));
      queryClient.invalidateQueries({
        queryKey: ["invoice-notifications", variables.invoiceId],
      });
    },
    onError: (error: Error) => {
      toast.error(t("فشل إرسال الفاتورة", "Failed to send invoice"), {
        description: error.message,
      });
    },
  });
}
